import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { defineMessages } from 'react-intl';

import { useCurrentUserCurrencies } from '@sorare/core/src/hooks/useCurrentUserCurrencies';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { isCryptocurrency } from '@sorare/core/src/lib/cryptocurrency';

import type { useCannotSettleInCrypto_anyCard } from './__generated__/useCannotSettleInCrypto.graphql.ts';

const messages = defineMessages({
  sellerDoesNotAcceptCrypto: {
    id: 'useCannotSettleInCrypto.sellerDoesNotAcceptCrypto',
    defaultMessage: 'The seller does not accept {cryptocurrencySymbol}',
  },
});

export const useCannotSettleInCrypto = () => {
  const {
    flags: { useCannotSwapCrytpo = true },
  } = useFeatureFlags();
  const { cryptocurrency } = useCurrentUserCurrencies();

  return (card: useCannotSettleInCrypto_anyCard) => {
    const settlementCurrencies =
      card?.liveSingleSaleOffer?.settlementCurrencies;
    if (!settlementCurrencies) return null;
    if (
      useCannotSwapCrytpo &&
      cryptocurrency &&
      !settlementCurrencies?.includes(cryptocurrency)
    )
      return messages.sellerDoesNotAcceptCrypto;
    if (
      !useCannotSwapCrytpo &&
      !settlementCurrencies?.some(s => isCryptocurrency(s))
    )
      return messages.sellerDoesNotAcceptCrypto;

    return null;
  };
};

useCannotSettleInCrypto.fragments = {
  anyCard: gql`
    fragment useCannotSettleInCrypto_anyCard on AnyCardInterface {
      slug
      liveSingleSaleOffer @skip(if: $onlyPrimary) {
        id
        settlementCurrencies
      }
    }
  ` as TypedDocumentNode<useCannotSettleInCrypto_anyCard>,
};
